import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import Form from 'components/Form';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { SeoRouteEditSeo_seoRoute$key } from '__generated__/SeoRouteEditSeo_seoRoute.graphql';
import LegacyEditor from 'components/LegacyEditor';

type FormFields = {
  metaTitle: string;
  metaDescription: string;
  h1: string;
  promoText: string;
  promoLinkTitle: string;
  promoLinkUrl: string;
};

type Props = {
  seoRoute: SeoRouteEditSeo_seoRoute$key;
};

const SeoRouteEditSeo: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const {
    id,
    metaTitle,
    metaDescription,
    h1,
    promoText,
    promoLinkTitle,
    promoLinkUrl,
  } = useFragment(
    graphql`
      fragment SeoRouteEditSeo_seoRoute on SeoRoute {
        id
        metaTitle
        metaDescription
        h1
        promoText
        promoLinkTitle
        promoLinkUrl
      }
    `,
    props.seoRoute
  );

  const [update, updating] = useMutation(graphql`
    mutation SeoRouteEditSeoMutation($id: ID!, $input: UpdateSeoRouteInput!) {
      updateSeoRoute(id: $id, input: $input) {
        ...SeoRouteSidebar_seoRoute
        ...SeoRouteEditSeo_seoRoute
      }
    }
  `);

  const initial = {
    metaTitle: metaTitle ?? '',
    metaDescription: metaDescription ?? '',
    h1: h1 ?? '',
    promoText: promoText ?? '',
    promoLinkTitle: promoLinkTitle ?? '',
    promoLinkUrl: promoLinkUrl ?? '',
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: { id, input },
        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="seoRouteEdit">
          <Section id="main" title="Bendra informacija">
            <Stack spacing={4}>
              <TextField fullWidth name="h1" label="H1" />
              <TextField fullWidth name="metaTitle" label="Meta title" />

              <TextField
                fullWidth
                name="metaDescription"
                label="Meta description"
              />

              <LegacyEditor name="promoText" label="Promo aprašymas" />

              <TextField
                fullWidth
                name="promoLinkTitle"
                label="Mygtuko tekstas"
              />

              <TextField fullWidth name="promoLinkUrl" label="Mygtuko URL" />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default SeoRouteEditSeo;
